import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Search, Coins, ShieldCheck, Gift, Mail, Trash2, ShieldOff, History, KeyRound } from 'lucide-react';
import { API_BASE_URL } from '../../services/api';

const ui = {
  border: 'rgba(255,255,255,0.07)',
  card: 'rgba(255,255,255,0.04)',
  cardHover: 'rgba(255,255,255,0.07)',
  input: 'rgba(255,255,255,0.06)',
  inputBorder: 'rgba(255,255,255,0.1)',
  muted: '#6b7280',
  label: '#9ca3af',
};

interface AdminUser {
  id: number;
  name: string;
  email: string;
  tokens_balance: number;
  is_active: boolean;
  is_free: boolean;
  is_admin: boolean;
  created_at: string | null;
}

interface TokenTx {
  id: number;
  type: string;
  amount: number;
  source: string;
  source_label: string;
  created_at: string | null;
}

const TX_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  credit: { label: 'Crédito', color: '#10b981' },
  debit: { label: 'Débito', color: '#ef4444' },
  set: { label: 'Definido', color: '#3b82f6' },
  adjustment: { label: 'Ajuste', color: '#f59e0b' },
};

const Badge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}>
    {label}
  </span>
);

const ActionCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="rounded-xl p-5" style={{ background: ui.card, border: `1px solid ${ui.border}` }}>
    <div className="flex items-center gap-2 mb-4">
      <span style={{ color: '#3b82f6' }}>{icon}</span>
      <span className="text-sm font-semibold text-white">{title}</span>
    </div>
    {children}
  </div>
);

const UsersDashboard: React.FC = () => {
  const token = localStorage.getItem('token');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  // Per-action state
  const [tokenValue, setTokenValue] = useState(0);
  const [tokenAdjust, setTokenAdjust] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Histórico de tokens
  const [tokenHistory, setTokenHistory] = useState<TokenTx[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Checar senha
  const [pwCheckValue, setPwCheckValue] = useState('');
  const [pwCheckResult, setPwCheckResult] = useState<'match' | 'nomatch' | null>(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    const r = await fetch(`${API_BASE_URL}/admin/users/${qs}`, { headers: authHeader });
    if (r.ok) {
      const d = await r.json();
      const users: AdminUser[] = d.items || [];
      setItems(users);
      // Refresh selected user from fresh data
      if (selected) {
        const fresh = users.find(u => u.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    }
    setLoading(false);
  }, [q, selected?.id]);

  useEffect(() => { load(); }, []);

  const showFeedback = (type: 'success' | 'error', text: string) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 3000);
  };

  const apiPost = async (path: string, label: string) => {
    setBusy(label);
    try {
      const r = await fetch(`${API_BASE_URL}/admin/users/${selected!.id}/${path}`, { method: 'POST', headers: authHeader });
      if (r.ok) {
        showFeedback('success', 'Salvo com sucesso');
        await load();
        if (path.startsWith('tokens/')) await loadTokenHistory(selected!.id);
      } else {
        showFeedback('error', 'Erro ao executar ação');
      }
    } catch {
      showFeedback('error', 'Erro de conexão');
    } finally {
      setBusy(null);
    }
  };

  const handleSetTokens = () => apiPost(`tokens/set?value=${tokenValue}`, 'setTokens');
  const handleAdjustTokens = () => apiPost(`tokens/adjust?amount=${tokenAdjust}`, 'adjustTokens');
  const handleToggleActive = () => apiPost(`status?is_active=${!selected!.is_active}`, 'status');
  const handleToggleFree = () => apiPost(`free?is_free=${!selected!.is_free}`, 'free');
  const handleToggleAdmin = () => apiPost(`admin?is_admin=${!selected!.is_admin}`, 'admin');
  const handleSendVerification = () => apiPost('send-verification', 'verification');

  const loadTokenHistory = async (userId: number) => {
    setHistoryLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/admin/users/${userId}/tokens/history`, { headers: authHeader });
      if (r.ok) {
        const d = await r.json();
        setTokenHistory(d.items || []);
      } else {
        setTokenHistory([]);
      }
    } catch {
      setTokenHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCheckPassword = async () => {
    if (!pwCheckValue) return;
    setBusy('checkPassword');
    setPwCheckResult(null);
    try {
      const r = await fetch(`${API_BASE_URL}/admin/users/${selected!.id}/check-password`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwCheckValue }),
      });
      if (r.ok) {
        const d = await r.json();
        setPwCheckResult(d.match ? 'match' : 'nomatch');
      } else {
        showFeedback('error', 'Erro ao verificar senha');
      }
    } catch {
      showFeedback('error', 'Erro de conexão');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    setBusy('delete');
    try {
      const r = await fetch(`${API_BASE_URL}/admin/users/${selected!.id}`, { method: 'DELETE', headers: authHeader });
      if (r.ok) {
        setSelected(null);
        setDeleteConfirm(false);
        await load();
      } else {
        showFeedback('error', 'Erro ao excluir');
      }
    } catch {
      showFeedback('error', 'Erro de conexão');
    } finally {
      setBusy(null);
    }
  };

  const selectUser = (u: AdminUser) => {
    setSelected(u);
    setTokenValue(u.tokens_balance ?? 0);
    setTokenAdjust(1);
    setDeleteConfirm(false);
    setFeedback(null);
    setPwCheckValue('');
    setPwCheckResult(null);
    loadTokenHistory(u.id);
  };

  const btnBase = "px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40";

  // ── Detail panel ──────────────────────────────────────────────────────────
  const DetailPanel = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={() => { setSelected(null); setDeleteConfirm(false); }}
          className="flex items-center gap-1 text-sm transition-colors"
          style={{ color: ui.muted }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e5e7eb'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = ui.muted; }}
        >
          <ChevronLeft className="w-4 h-4" /> Usuários
        </button>
      </div>

      <div className="rounded-xl p-5" style={{ background: ui.card, border: `1px solid ${ui.border}` }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-white font-semibold text-base">{selected!.name}</p>
            <p className="text-sm mt-0.5" style={{ color: ui.muted }}>{selected!.email}</p>
            {selected!.created_at && (
              <p className="text-xs mt-1" style={{ color: ui.label }}>
                Cadastro: {new Date(selected!.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge label={`${selected!.tokens_balance ?? 0} tokens`} color="#3b82f6" />
            {selected!.is_active ? <Badge label="Ativo" color="#10b981" /> : <Badge label="Inativo" color="#ef4444" />}
            {selected!.is_free && <Badge label="Gratuito" color="#f59e0b" />}
            {selected!.is_admin && <Badge label="Admin" color="#8b5cf6" />}
          </div>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`px-4 py-2 rounded-lg text-sm font-medium ${feedback.type === 'success' ? 'text-green-300' : 'text-red-300'}`}
          style={{ background: feedback.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${feedback.type === 'success' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
          {feedback.text}
        </div>
      )}

      {/* Tokens — definir */}
      <ActionCard icon={<Coins className="w-4 h-4" />} title="Tokens">
        <div className="space-y-4">
          <div>
            <p className="text-xs mb-2" style={{ color: ui.label }}>Definir saldo exato</p>
            <div className="flex gap-2">
              <input
                type="number"
                value={tokenValue}
                onChange={e => setTokenValue(Number(e.target.value))}
                className="flex-1 px-3 py-2 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
              />
              <button
                disabled={busy === 'setTokens'}
                onClick={handleSetTokens}
                className={`${btnBase} text-white`}
                style={{ background: '#2563eb' }}
              >
                {busy === 'setTokens' ? 'Salvando…' : 'Definir'}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs mb-2" style={{ color: ui.label }}>Ajuste (valor positivo = adicionar, negativo = remover)</p>
            <div className="flex gap-2">
              <input
                type="number"
                value={tokenAdjust}
                onChange={e => setTokenAdjust(Number(e.target.value))}
                className="flex-1 px-3 py-2 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
              />
              <button
                disabled={busy === 'adjustTokens'}
                onClick={handleAdjustTokens}
                className={`${btnBase} text-white`}
                style={{ background: '#16a34a' }}
              >
                {busy === 'adjustTokens' ? 'Salvando…' : 'Ajustar'}
              </button>
            </div>
          </div>
        </div>
      </ActionCard>

      {/* Histórico de tokens */}
      <ActionCard icon={<History className="w-4 h-4" />} title="Histórico de Tokens">
        {historyLoading ? (
          <p className="text-sm" style={{ color: ui.muted }}>Carregando…</p>
        ) : tokenHistory.length === 0 ? (
          <p className="text-sm" style={{ color: ui.muted }}>Nenhuma movimentação de tokens ainda.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {tokenHistory.map(tx => {
              const t = TX_TYPE_LABELS[tx.type] || { label: tx.type, color: ui.muted };
              return (
                <div key={tx.id} className="flex items-center justify-between gap-3 py-2" style={{ borderBottom: `1px solid ${ui.border}` }}>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{tx.source_label}</p>
                    <p className="text-xs mt-0.5" style={{ color: ui.muted }}>
                      {tx.created_at
                        ? new Date(tx.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge label={t.label} color={t.color} />
                    <span className="text-sm font-mono" style={{ color: t.color }}>
                      {tx.type === 'debit' ? '-' : tx.type === 'credit' ? '+' : ''}{tx.amount}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ActionCard>

      {/* Status da conta */}
      <ActionCard icon={<ShieldCheck className="w-4 h-4" />} title="Status da Conta">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">{selected!.is_active ? 'Conta ativa' : 'Conta inativa'}</p>
            <p className="text-xs mt-0.5" style={{ color: ui.muted }}>
              {selected!.is_active ? 'O usuário pode fazer login normalmente.' : 'O usuário não consegue fazer login.'}
            </p>
          </div>
          <button
            disabled={busy === 'status'}
            onClick={handleToggleActive}
            className={`${btnBase} text-white flex-shrink-0`}
            style={{ background: selected!.is_active ? '#7f1d1d' : '#14532d' }}
          >
            {busy === 'status' ? '…' : selected!.is_active ? 'Desativar' : 'Ativar'}
          </button>
        </div>
      </ActionCard>

      {/* Conta gratuita */}
      <ActionCard icon={<Gift className="w-4 h-4" />} title="Conta Gratuita">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">{selected!.is_free ? 'Acesso gratuito ativo' : 'Acesso gratuito desativado'}</p>
            <p className="text-xs mt-0.5" style={{ color: ui.muted }}>Consultas sem consumir tokens.</p>
          </div>
          <button
            disabled={busy === 'free'}
            onClick={handleToggleFree}
            className={`${btnBase} text-white flex-shrink-0`}
            style={{ background: selected!.is_free ? '#78350f' : '#1e3a5f' }}
          >
            {busy === 'free' ? '…' : selected!.is_free ? 'Remover' : 'Tornar gratuito'}
          </button>
        </div>
      </ActionCard>

      {/* Admin */}
      <ActionCard icon={<ShieldOff className="w-4 h-4" />} title="Permissão Admin">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">{selected!.is_admin ? 'Usuário é administrador' : 'Usuário comum'}</p>
            <p className="text-xs mt-0.5" style={{ color: ui.muted }}>Acesso ao painel de administração.</p>
          </div>
          <button
            disabled={busy === 'admin'}
            onClick={handleToggleAdmin}
            className={`${btnBase} text-white flex-shrink-0`}
            style={{ background: selected!.is_admin ? '#4c1d95' : '#1e1b4b' }}
          >
            {busy === 'admin' ? '…' : selected!.is_admin ? 'Remover admin' : 'Tornar admin'}
          </button>
        </div>
      </ActionCard>

      {/* E-mail de verificação */}
      <ActionCard icon={<Mail className="w-4 h-4" />} title="Verificação de E-mail">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white">Reenviar e-mail de verificação</p>
            <p className="text-xs mt-0.5" style={{ color: ui.muted }}>Gera novo código de 6 dígitos e envia para o usuário.</p>
          </div>
          <button
            disabled={busy === 'verification'}
            onClick={handleSendVerification}
            className={`${btnBase} text-white flex-shrink-0`}
            style={{ background: '#1e3a5f' }}
          >
            {busy === 'verification' ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </ActionCard>

      {/* Verificar senha */}
      <ActionCard icon={<KeyRound className="w-4 h-4" />} title="Verificar Senha">
        <div className="space-y-3">
          <p className="text-xs" style={{ color: ui.muted }}>
            Confere se a senha informada bate com a do usuário, sem expor a senha real.
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={pwCheckValue}
              onChange={e => { setPwCheckValue(e.target.value); setPwCheckResult(null); }}
              onKeyDown={e => e.key === 'Enter' && handleCheckPassword()}
              placeholder="Digite a senha para conferir"
              className="flex-1 px-3 py-2 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
            />
            <button
              disabled={busy === 'checkPassword' || !pwCheckValue}
              onClick={handleCheckPassword}
              className={`${btnBase} text-white flex-shrink-0`}
              style={{ background: '#2563eb' }}
            >
              {busy === 'checkPassword' ? 'Checando…' : 'Checar'}
            </button>
          </div>
          {pwCheckResult && (
            <p className="text-sm font-medium" style={{ color: pwCheckResult === 'match' ? '#10b981' : '#ef4444' }}>
              {pwCheckResult === 'match' ? '✅ Senha correta' : '❌ Senha incorreta'}
            </p>
          )}
        </div>
      </ActionCard>

      {/* Zona de perigo */}
      <ActionCard icon={<Trash2 className="w-4 h-4" style={{ color: '#ef4444' }} />} title="Zona de Perigo">
        {!deleteConfirm ? (
          <button
            onClick={() => setDeleteConfirm(true)}
            className="w-full py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}
          >
            Excluir conta permanentemente
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-red-300">
              Todos os dados de <strong>{selected!.name}</strong> serão apagados. Isso não pode ser desfeito.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                disabled={busy === 'delete'}
                className={`${btnBase} flex-1`}
                style={{ background: 'rgba(255,255,255,0.06)', color: '#9ca3af' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={busy === 'delete'}
                className={`${btnBase} flex-1 text-white`}
                style={{ background: busy === 'delete' ? 'rgba(239,68,68,0.4)' : '#b91c1c' }}
              >
                {busy === 'delete' ? 'Excluindo…' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        )}
      </ActionCard>
    </div>
  );

  // ── List ──────────────────────────────────────────────────────────────────
  const ListPanel = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ui.muted }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Buscar por nome ou e-mail"
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ background: ui.input, border: `1px solid ${ui.inputBorder}` }}
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={`${btnBase} text-white flex-shrink-0`}
          style={{ background: '#2563eb' }}
        >
          {loading ? '…' : 'Buscar'}
        </button>
      </div>

      <p className="text-xs" style={{ color: ui.muted }}>{items.length} usuário{items.length !== 1 ? 's' : ''}</p>

      <div className="space-y-2">
        {items.map(u => (
          <button
            key={u.id}
            onClick={() => selectUser(u)}
            className="w-full text-left rounded-xl px-4 py-3 transition-colors flex items-center gap-4"
            style={{ background: ui.card, border: `1px solid ${ui.border}` }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ui.cardHover; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ui.card; }}
          >
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}>
              {(u.name || u.email).charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{u.name}</p>
              <p className="text-xs truncate" style={{ color: ui.muted }}>{u.email}</p>
            </div>

            {/* Badges */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>{u.tokens_balance ?? 0}t</span>
              {!u.is_active && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Inativo" />}
              {u.is_free && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" title="Gratuito" />}
              {u.is_admin && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" title="Admin" />}
            </div>

            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: ui.muted }} />
          </button>
        ))}

        {items.length === 0 && !loading && (
          <p className="text-center py-10 text-sm" style={{ color: ui.muted }}>Nenhum usuário encontrado.</p>
        )}
      </div>
    </div>
  );

  return selected ? <DetailPanel /> : <ListPanel />;
};

export default UsersDashboard;
